import { c as _c } from "react/compiler-runtime";
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useChatMessages } from '@ai-sdk-tools/store';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useIsMobile } from '@newfront-insurance/admin-ui';
import { isEmpty, isNotEmpty } from '@newfront-insurance/core';
import { CircleSpinnerInline, IconAdd2, SegmentedControlV2, useFuzzy } from '@newfront-insurance/core-ui';
import { colors } from '@newfront-insurance/core-ui';
import { cn, IconButtonAsButton, IconSearch, NewfrontLogo, ScrollArea } from '@newfront-insurance/core-ui/v2';
import { Settings, X } from 'lucide-react';
import { createContext, Fragment, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IconSidebar } from './icons';
import { OrphanedStreamsContainer } from './orphaned-streams-container';
import type { GetChatsByFilters } from '../../../queries/chats';
import { GET_CHATS_BY_FILTERS } from '../../../queries/chats';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { groupChatsByDate } from '../lib/chat-utils';
import { normalizeChatUuid } from '../lib/notification-helpers';
const SIDEBAR_TRANSITION_DURATION = 200;
interface ChatSidebarContextValue {
  isSidebarExpanded: boolean;
  /** True only after the width transition completes when expanding. Use for text/content rendering to prevent reflow. */
  isContentExpanded: boolean;
  chatHistoryFilter: 'current-account' | 'all-accounts';
  setChatHistoryFilter: (value: 'current-account' | 'all-accounts') => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  searchResults: GetChatsByFilters;
  isLoadingChats: boolean;
}
const ChatSidebarContext = createContext<ChatSidebarContextValue | null>(null);
function useChatSidebarContext() {
  const context = useContext(ChatSidebarContext);
  if (!context) {
    throw new Error("ChatSidebar components must be used within ChatSidebar");
  }
  return context;
}
interface ChatSidebarProps {
  className?: string;
  children?: ReactNode;
}

/**
 * Chat sidebar component with history, search, and filtering.
 *
 * On desktop, the sidebar is always visible in one of two states:
 * - **Collapsed** (63px): Shows only icon buttons (New Chat, Settings, Search)
 * - **Expanded** (253px): Shows full sidebar with labels, chat history, and filters
 *
 * On mobile, the sidebar preserves the previous overlay behavior:
 * hidden by default, shown as an overlay when toggled.
 *
 * The `isSidebarOpen` context value controls:
 * - Desktop: collapsed (false) vs expanded (true)
 * - Mobile: hidden (false) vs overlay (true)
 */
export function ChatSidebar(t0) {
  const $ = _c(81);
  const {
    className,
    children
  } = t0;
  const {
    isMobile
  } = useIsMobile();
  const {
    userUuid,
    accountUuid,
    setIsSidebarOpen,
    isSidebarOpen,
    isFullscreen,
    isStandaloneMode,
    appType,
    hasNotifications
  } = useChatOverlayContext();
  const isSidebarExpanded = isSidebarOpen;
  const [isContentExpanded, setIsContentExpanded] = useState(isSidebarExpanded);
  let t1;
  let t2;
  if ($[0] !== isSidebarExpanded) {
    t1 = () => {
      if (isSidebarExpanded) {
        const timer = setTimeout(() => setIsContentExpanded(true), SIDEBAR_TRANSITION_DURATION);
        return () => clearTimeout(timer);
      }
      setIsContentExpanded(false);
    };
    t2 = [isSidebarExpanded];
    $[0] = isSidebarExpanded;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const [chatHistoryFilter, setChatHistoryFilter] = useState(isEmpty(accountUuid) ? "all-accounts" : "current-account");
  const t3 = appType ?? undefined;
  let t4;
  if ($[3] !== t3 || $[4] !== userUuid) {
    t4 = {
      userUuid,
      appType: t3
    };
    $[3] = t3;
    $[4] = userUuid;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== userUuid) {
    t5 = isEmpty(userUuid);
    $[6] = userUuid;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = {
      variables: t4,
      skip: t5,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network"
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    data,
    networkStatus
  } = useQuery(GET_CHATS_BY_FILTERS, t6);
  let t7;
  if ($[11] !== data) {
    t7 = data ?? {};
    $[11] = data;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    chatsList: t8
  } = t7;
  let t9;
  if ($[13] !== t8) {
    t9 = t8 === undefined ? [] : t8;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const chats = t9;
  const isLoadingChats = networkStatus === NetworkStatus.loading && data === undefined;
  let t10;
  let t11;
  if ($[15] !== accountUuid || $[16] !== chatHistoryFilter || $[17] !== chats) {
    t11 = chatHistoryFilter === "current-account" && isNotEmpty(accountUuid) ? [...chats].filter(chat => chat.accountUuid === accountUuid) : [...chats];
    $[15] = accountUuid;
    $[16] = chatHistoryFilter;
    $[17] = chats;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  t10 = t11;
  const chatsFilteredByAccount = t10;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      keys: ["title", "summary"]
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const fuzzySearch = useFuzzy(chatsFilteredByAccount, t12);
  let t13;
  let t14;
  if ($[20] !== chatHistoryFilter || $[21] !== fuzzySearch.query || $[22] !== fuzzySearch.results || $[23] !== fuzzySearch.search || $[24] !== isContentExpanded || $[25] !== isLoadingChats || $[26] !== isSidebarExpanded) {
    t14 = {
      isSidebarExpanded,
      isContentExpanded,
      chatHistoryFilter,
      setChatHistoryFilter,
      searchQuery: fuzzySearch.query,
      setSearchQuery: fuzzySearch.search,
      searchResults: fuzzySearch.results,
      isLoadingChats
    };
    $[20] = chatHistoryFilter;
    $[21] = fuzzySearch.query;
    $[22] = fuzzySearch.results;
    $[23] = fuzzySearch.search;
    $[24] = isContentExpanded;
    $[25] = isLoadingChats;
    $[26] = isSidebarExpanded;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  t13 = t14;
  const contextValue = t13;
  if (isMobile && !isSidebarOpen) {
    return null;
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <><ChatSidebarNewChat /><ChatSidebarSearch /><ChatSidebarFilterTabs /><ChatSidebarHistory /></>;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const defaultChildren = t15;
  let t16;
  if ($[29] !== isMobile || $[30] !== isSidebarOpen || $[31] !== setIsSidebarOpen) {
    t16 = isSidebarOpen && isMobile && <div className="fixed inset-0 z-[1299] bg-[#000]/70 opacity-50 backdrop-blur-xs" onClick={() => setIsSidebarOpen(false)} />;
    $[29] = isMobile;
    $[30] = isSidebarOpen;
    $[31] = setIsSidebarOpen;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = !isFullscreen;
  const t18 = isFullscreen && !isMobile;
  const t19 = isFullscreen && isMobile;
  let t20;
  if ($[33] !== t17 || $[34] !== t18 || $[35] !== t19) {
    t20 = cn("absolute -top-px h-[calc(100%+2px)]", {
      "right-full z-[-1]": t17,
      "z-overlay left-0": t18,
      "z-overlay right-0 left-0": t19
    });
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const t21 = isSidebarExpanded ? "w-[253px]" : "w-[63px]";
  const t22 = !isFullscreen && !isStandaloneMode && "shadow-floating-bar [clip-path:inset(-20px_0px_-20px_-20px)]";
  let t23;
  if ($[37] !== className || $[38] !== t21 || $[39] !== t22) {
    t23 = cn("border-border/50 relative flex h-full shrink-0 flex-col rounded-xs rounded-r-none border-y border-l", "bg-steel-50 dark:bg-steel-500", "overflow-hidden transition-[width] duration-200", t21, t22, className);
    $[37] = className;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  const t24 = isContentExpanded ? "justify-between" : "justify-start";
  let t25;
  if ($[41] !== t24) {
    t25 = cn("border-border/50 flex h-14 shrink-0 items-center border-b px-[18px]", "text-muted-foreground", t24);
    $[41] = t24;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== isContentExpanded) {
    t26 = isContentExpanded && <h1 className="text-muted-foreground animate-in fade-in text-sm font-semibold whitespace-nowrap duration-200">Nova Assist</h1>;
    $[43] = isContentExpanded;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== isSidebarOpen || $[46] !== setIsSidebarOpen) {
    t27 = () => setIsSidebarOpen(!isSidebarOpen);
    $[45] = isSidebarOpen;
    $[46] = setIsSidebarOpen;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <IconSidebar />;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] !== t27) {
    t29 = <IconButtonAsButton onClick={t27} className="hover:bg-steel-200/20 shrink-0">{t28}</IconButtonAsButton>;
    $[49] = t27;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== hasNotifications || $[52] !== isContentExpanded) {
    t30 = !isContentExpanded && hasNotifications && <span className="bg-rebrand-400 absolute top-0 right-0 size-2 rounded-full" />;
    $[51] = hasNotifications;
    $[52] = isContentExpanded;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== t29 || $[55] !== t30) {
    t31 = <div className="relative">{t29}{t30}</div>;
    $[54] = t29;
    $[55] = t30;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== t25 || $[58] !== t26 || $[59] !== t31) {
    t32 = <header className={t25}>{t26}{t31}</header>;
    $[57] = t25;
    $[58] = t26;
    $[59] = t31;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  const t33 = children ?? defaultChildren;
  let t34;
  if ($[61] !== t33) {
    t34 = <div className="flex min-h-0 flex-1 flex-col gap-2 px-2 pt-5">{t33}</div>;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== isContentExpanded || $[64] !== isStandaloneMode) {
    t35 = isStandaloneMode && isContentExpanded && <div className={cn("pointer-events-none absolute inset-x-0 bottom-0 h-20 rounded-b-xs shadow-lg", "from-steel-50 via-steel-50/95 bg-gradient-to-t to-transparent", "dark:from-steel-500 dark:via-steel-500/95 dark:to-transparent")}><div className="flex h-full items-end justify-center pb-3"><NewfrontLogo /></div></div>;
    $[63] = isContentExpanded;
    $[64] = isStandaloneMode;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== t23 || $[67] !== t32 || $[68] !== t34 || $[69] !== t35) {
    t36 = <div className={t23}>{t32}{t34}{t35}</div>;
    $[66] = t23;
    $[67] = t32;
    $[68] = t34;
    $[69] = t35;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] !== t20 || $[72] !== t36) {
    t37 = <div className={t20}>{t36}</div>;
    $[71] = t20;
    $[72] = t36;
    $[73] = t37;
  } else {
    t37 = $[73];
  }
  let t38;
  if ($[74] !== chats) {
    t38 = chats.length > 0 && <OrphanedStreamsContainer chats={chats} />;
    $[74] = chats;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  let t39;
  if ($[76] !== contextValue || $[77] !== t16 || $[78] !== t37 || $[79] !== t38) {
    t39 = <ChatSidebarContext.Provider value={contextValue}>{t16}{t37}{t38}</ChatSidebarContext.Provider>;
    $[76] = contextValue;
    $[77] = t16;
    $[78] = t37;
    $[79] = t38;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  return t39;
}

/**
 * New Chat button component for the sidebar.
 * Shows icon-only when collapsed, icon + text when expanded.
 */
export function ChatSidebarNewChat() {
  const $ = _c(13);
  const {
    startNewChat,
    setIsSidebarOpen,
    setIsSettingsOpen,
    isSettingsOpen
  } = useChatOverlayContext();
  const {
    isMobile
  } = useIsMobile();
  const {
    isContentExpanded
  } = useChatSidebarContext();
  const messages = useChatMessages();
  const hasAnyMessages = messages.length > 0;
  const isEnabled = hasAnyMessages || isSettingsOpen;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IconAdd2 color={colors.brand[500]} size={16} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      background: "linear-gradient(97deg, #A4F5FF 0%, #E7FC53 122.24%)"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isMobile || $[3] !== setIsSettingsOpen || $[4] !== setIsSidebarOpen || $[5] !== startNewChat) {
    t2 = () => {
      startNewChat();
      setIsSettingsOpen(false);
      if (isMobile) {
        setIsSidebarOpen(false);
      }
    };
    $[2] = isMobile;
    $[3] = setIsSettingsOpen;
    $[4] = setIsSidebarOpen;
    $[5] = startNewChat;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = !isEnabled;
  let t4;
  if ($[7] !== isContentExpanded) {
    t4 = isContentExpanded && <span className="text-brand-500 animate-in fade-in pl-1 whitespace-nowrap duration-200">New chat</span>;
    $[7] = isContentExpanded;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <MenuItem icon={t0} className="font-semibold" iconClassName="rounded-full" iconStyle={t1} onClick={t2} disabled={t3}>{t4}</MenuItem>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}

/**
 * Built-in settings button for the sidebar.
 * Shows icon-only when collapsed, icon + text when expanded.
 * Toggles the settings panel via context.
 */
export function ChatSidebarSettingsButton() {
  const $ = _c(12);
  const {
    isSettingsOpen,
    setIsSettingsOpen,
    setIsSidebarOpen
  } = useChatOverlayContext();
  const {
    isMobile
  } = useIsMobile();
  const {
    isContentExpanded
  } = useChatSidebarContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Settings className="size-5" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== isMobile || $[2] !== isSettingsOpen || $[3] !== setIsSettingsOpen || $[4] !== setIsSidebarOpen) {
    t1 = () => {
      setIsSettingsOpen(!isSettingsOpen);
      if (isMobile) {
        setIsSidebarOpen(false);
      }
    };
    $[1] = isMobile;
    $[2] = isSettingsOpen;
    $[3] = setIsSettingsOpen;
    $[4] = setIsSidebarOpen;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== isContentExpanded) {
    t2 = isContentExpanded && <span className="animate-in fade-in whitespace-nowrap duration-200">Settings</span>;
    $[6] = isContentExpanded;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== isSettingsOpen || $[9] !== t1 || $[10] !== t2) {
    t3 = <MenuItem icon={t0} isActive={isSettingsOpen} onClick={t1}>{t2}</MenuItem>;
    $[8] = isSettingsOpen;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}

/**
 * Search component for the sidebar.
 * In collapsed mode: shows search icon that expands the sidebar when clicked.
 * In expanded mode: search is integrated into the history section titles (see ChatSidebarHistory).
 */
export function ChatSidebarSearch() {
  const $ = _c(3);
  const {
    setIsSidebarOpen
  } = useChatOverlayContext();
  const {
    isContentExpanded
  } = useChatSidebarContext();
  if (isContentExpanded) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IconSearch className="text-steel-200 size-5" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== setIsSidebarOpen) {
    t1 = <MenuItem icon={t0} onClick={() => setIsSidebarOpen(true)} />;
    $[1] = setIsSidebarOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * Filter tabs component for the sidebar.
 * Hidden when sidebar is collapsed.
 */
export function ChatSidebarFilterTabs() {
  const $ = _c(24);
  const {
    accountUuid
  } = useChatOverlayContext();
  const {
    isContentExpanded,
    chatHistoryFilter,
    setChatHistoryFilter
  } = useChatSidebarContext();
  if (!isContentExpanded) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SectionTitle className="px-3">Chat History</SectionTitle>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== accountUuid) {
    t1 = isEmpty(accountUuid);
    $[1] = accountUuid;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      label: "Current account",
      value: "current-account",
      disabled: t1
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== accountUuid) {
    t3 = isEmpty(accountUuid);
    $[5] = accountUuid;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = {
      label: "All accounts",
      value: "all-accounts",
      disabled: t3
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = [t2, t4];
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== setChatHistoryFilter) {
    t6 = value => {
      setChatHistoryFilter(value as 'current-account' | 'all-accounts');
    };
    $[12] = setChatHistoryFilter;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== accountUuid || $[15] !== chatHistoryFilter) {
    t7 = isEmpty(accountUuid) ? "all-accounts" : chatHistoryFilter;
    $[14] = accountUuid;
    $[15] = chatHistoryFilter;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== accountUuid) {
    t8 = cn("!border-border/50 !bg-background mx-2 !h-8 !rounded-full", {
      "opacity-50": isEmpty(accountUuid)
    });
    $[17] = accountUuid;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t9 = <div className="mt-2 flex flex-col gap-2">{t0}<SegmentedControlV2 options={t5} onChange={t6} value={t7} activeColor="default" containerClassname={t8} optionClassname="!rounded-full !px-1.5 !border-transparent [&.isActive]:!bg-steel-100 !text-sm" /></div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}

/**
 * Chat history list component for the sidebar.
 * Hidden when sidebar is collapsed.
 * Includes inline search: a magnifying glass on the first date group header
 * that expands into a search input overlay when clicked.
 */
export function ChatSidebarHistory() {
  const {
    isMobile
  } = useIsMobile();
  const {
    chatUuid,
    setIsSidebarOpen,
    setActiveChat,
    setIsSettingsOpen,
    isStandaloneMode
  } = useChatOverlayContext();
  const {
    isContentExpanded,
    searchQuery,
    setSearchQuery,
    searchResults,
    isLoadingChats
  } = useChatSidebarContext();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const groupedChats = useMemo(() => groupChatsByDate(searchResults), [searchResults]);

  // Auto-focus the search input when opened
  useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);
  if (!isContentExpanded) {
    return null;
  }
  const handleCloseSearch = () => {
    setSearchQuery('');
    setIsSearchOpen(false);
  };
  return <ScrollArea className={cn('-mx-2 mt-4 min-h-0 flex-1', isStandaloneMode && 'mb-20')}>
      <div className="flex flex-col gap-2 px-2 pb-5">
        {isLoadingChats && <MenuItem icon={<CircleSpinnerInline />} className="text-foreground">
            Loading chats...
          </MenuItem>}
        {/* First section title — always rendered so search has a stable anchor */}
        <SectionTitle>
          {isSearchOpen ? <div className="flex flex-1 items-center gap-1.5">
              <IconSearch className="text-steel-300 size-4 shrink-0" />
              <input ref={searchInputRef} className="text-foreground placeholder:text-steel-300 min-w-0 flex-1 bg-transparent text-sm font-normal tracking-normal outline-none" placeholder="Search chats" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              <button type="button" onClick={handleCloseSearch} className="text-steel-300 hover:text-steel-400 shrink-0">
                <X className="size-3.5" />
              </button>
            </div> : <>
              <span className="flex-1">{groupedChats[0]?.title ?? 'Today'}</span>
              <button type="button" onClick={() => setIsSearchOpen(true)} className="text-steel-300 hover:text-steel-400 shrink-0">
                <IconSearch className="size-4" />
              </button>
            </>}
        </SectionTitle>
        {/* Chat groups — first group title already rendered above */}
        {groupedChats.map((group, groupIndex) => <Fragment key={group.title}>
            {groupIndex > 0 && <SectionTitle>{group.title}</SectionTitle>}
            <div className="flex flex-col gap-2 px-3">
              {group.chats.map(chat => {
            const isActive = normalizeChatUuid(chat.uuid ?? '') === normalizeChatUuid(chatUuid);
            return <ChatMenuItem key={chat.uuid} onClick={() => {
              setActiveChat(chat.uuid!);
              setIsSettingsOpen(false);
              if (isMobile) {
                setIsSidebarOpen(false);
              }
            }} isActive={isActive} className="text-foreground" hasNotifications={chat.hasUnreadMessages as boolean} isInProgress={chat.activeStreamId !== null}>
                    {chat.title}
                  </ChatMenuItem>;
          })}
            </div>
          </Fragment>)}
        {isSearchOpen && groupedChats.length === 0 && !isLoadingChats && <p className="text-steel-300 px-6 text-sm">No results found</p>}
      </div>
    </ScrollArea>;
}

/**
 * Settings wrapper component for the sidebar.
 * Provides a slot for apps to add their own settings components.
 * @deprecated Use ChatSidebarSettingsButton for the built-in settings toggle.
 */
export function ChatSidebarSettings(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  const {
    isContentExpanded
  } = useChatSidebarContext();
  if (!isContentExpanded) {
    return null;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex shrink-0 flex-col gap-3 px-4">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function SectionTitle(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("sticky top-0 z-10 flex items-center gap-2 px-3 text-sm font-semibold tracking-widest", "bg-steel-50 text-steel-300 dark:bg-steel-500 dark:text-steel-400 uppercase", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function MenuItem(t0) {
  const $ = _c(14);
  const {
    children,
    icon,
    className,
    iconClassName,
    iconStyle,
    onClick,
    disabled,
    isActive
  } = t0;
  const t1 = !onClick;
  let t2;
  if ($[0] !== className || $[1] !== isActive || $[2] !== t1) {
    t2 = cn("text-muted-foreground relative flex items-center gap-2 px-2 py-1.5 text-start", "hover:bg-steel-100 dark:hover:bg-steel-400 rounded-xs", "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent", {
      "bg-steel-400/10 hover:bg-steel-400/10 cursor-default font-medium": isActive,
      "cursor-default hover:bg-transparent": t1
    }, className);
    $[0] = className;
    $[1] = isActive;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isActive ? undefined : onClick;
  let t4;
  if ($[4] !== icon || $[5] !== iconClassName || $[6] !== iconStyle) {
    t4 = icon && <div className={cn("flex size-6 shrink-0 items-center justify-center", iconClassName)} style={iconStyle}>{icon}</div>;
    $[4] = icon;
    $[5] = iconClassName;
    $[6] = iconStyle;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children || $[9] !== disabled || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <button type="button" className={t2} onClick={t3} disabled={disabled}>{t4}{children}</button>;
    $[8] = children;
    $[9] = disabled;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function ChatMenuItem(t0) {
  const $ = _c(21);
  const {
    children,
    icon,
    className,
    iconClassName,
    iconStyle,
    onClick,
    disabled,
    isActive,
    hasNotifications,
    isInProgress
  } = t0;
  const t1 = !onClick;
  let t2;
  if ($[0] !== className || $[1] !== isActive || $[2] !== t1) {
    t2 = cn("text-muted-foreground relative flex items-center justify-between gap-2 px-2 py-1.5 text-start", "hover:bg-steel-100 dark:hover:bg-steel-400 rounded-xs", "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent", {
      "bg-steel-400/10 hover:bg-steel-400/10 cursor-default font-medium": isActive,
      "cursor-default hover:bg-transparent": t1
    }, className);
    $[0] = className;
    $[1] = isActive;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isActive ? undefined : onClick;
  let t4;
  if ($[4] !== icon || $[5] !== iconClassName || $[6] !== iconStyle) {
    t4 = icon && <div className={cn("flex size-6 shrink-0 items-center justify-center", iconClassName)} style={iconStyle}>{icon}</div>;
    $[4] = icon;
    $[5] = iconClassName;
    $[6] = iconStyle;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== hasNotifications) {
    t5 = hasNotifications && <span className="bg-rebrand-400 h-2 max-h-2 min-h-2 w-2 max-w-2 min-w-2 rounded-full" />;
    $[8] = hasNotifications;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== hasNotifications || $[11] !== isInProgress) {
    t6 = isInProgress && !hasNotifications && <CircleSpinnerInline size={12} />;
    $[10] = hasNotifications;
    $[11] = isInProgress;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== children || $[14] !== disabled || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t7 = <button type="button" className={t2} onClick={t3} disabled={disabled}>{t4}{children}{t5}{t6}</button>;
    $[13] = children;
    $[14] = disabled;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}